import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessagesAPI } from "../api/messages";
import {
  DEFAULT_MESSAGE_CATEGORIES,
  type CallbackSlot,
  type ClientMessage,
  type MessageCategory,
  type MessageEntry,
  type MessageHistoryEntry,
  type MessageResolution,
  type MessageUrgency,
  type MessageVisibility,
} from "../types";
import { isDemoMode } from "@/features/utils/demo-mode";
import {
  assignMockMessage,
  getMockCallbackSlot,
  getMockMessage,
  handleMockMessage,
  listMockMessages,
  addMockEntry,
  deleteMockEntry,
  setMockEntryVisibility,
  getMockHistory,
  listMockEntries,
  setMockTask,
  triageMockMessage,
} from "./use-messages.mock";

// Query Keys
export const messageKeys = {
  all: ["messages"] as const,
  lists: () => [...messageKeys.all, "list"] as const,
  list: (clientId: string, filters?: Record<string, unknown>) =>
    [...messageKeys.lists(), clientId, filters] as const,
  details: () => [...messageKeys.all, "detail"] as const,
  detail: (clientId: string, id: string) =>
    [...messageKeys.details(), clientId, id] as const,
  entries: (clientId: string, id: string) =>
    [...messageKeys.all, "entries", clientId, id] as const,
  history: (clientId: string, id: string) =>
    [...messageKeys.all, "history", clientId, id] as const,
  callbackSlot: (clientId: string, id: string) =>
    [...messageKeys.all, "callback-slot", clientId, id] as const,
  categories: (clientId: string) =>
    [...messageKeys.all, "categories", clientId] as const,
};

/** The tenant's categories, the built-in ones until it configured its own. */
export function useMessageCategories() {
  const clientId = useCurrentClient()?.client?.id || "";
  return useQuery<MessageCategory[]>({
    queryKey: messageKeys.categories(clientId),
    queryFn: async () =>
      isDemoMode()
        ? DEFAULT_MESSAGE_CATEGORIES
        : (await MessagesAPI.listCategories(clientId)).categories,
    staleTime: 5 * 60 * 1000,
    enabled: !!clientId,
  });
}

// The inbox, or one customer's requests with `customerId`
export function useMessages(
  options?: { status?: string; assignedMemberId?: string; customerId?: string },
  queryOptions?: { enabled?: boolean },
) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<{ messages: ClientMessage[]; total: number }>({
    queryKey: messageKeys.list(clientId, options),
    queryFn: () =>
      isDemoMode()
        ? Promise.resolve(listMockMessages(options))
        : MessagesAPI.listMessages(clientId, options),
    staleTime: 30 * 1000,
    enabled: !!clientId && queryOptions?.enabled !== false,
  });
}

/**
 * One request, for a link that lands on a request neither list holds — the
 * inbox reads its rows from the lists it already loaded.
 */
export function useMessage(id: string, queryOptions?: { enabled?: boolean }) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<{ message: ClientMessage }>({
    queryKey: messageKeys.detail(clientId, id),
    queryFn: () =>
      isDemoMode()
        ? Promise.resolve(getMockMessage(id))
        : MessagesAPI.getMessage(clientId, id),
    staleTime: 30 * 1000,
    enabled: !!clientId && !!id && queryOptions?.enabled !== false,
  });
}

/**
 * The call back time this request would get.
 *
 * Fetched only when the form that shows it is open: it walks the customer's
 * preferences and the store's schedule, and nothing on the list needs it.
 */
export function useCallbackSlot(messageId: string, enabled: boolean) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<CallbackSlot>({
    queryKey: messageKeys.callbackSlot(clientId, messageId),
    queryFn: () =>
      isDemoMode()
        ? Promise.resolve(getMockCallbackSlot())
        : MessagesAPI.getCallbackSlot(clientId, messageId),
    enabled: !!clientId && !!messageId && enabled,
    staleTime: 60 * 1000,
  });
}

// The request's timeline: comments, the agent's updates, what happened to it
export function useMessageEntries(id: string) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<{ entries: MessageEntry[] }>({
    queryKey: messageKeys.entries(clientId, id),
    queryFn: () =>
      isDemoMode()
        ? Promise.resolve(listMockEntries(id))
        : MessagesAPI.listEntries(clientId, id),
    staleTime: 15 * 1000,
    enabled: !!clientId && !!id,
  });
}

// What changed on the request itself — the audit trail, read only when asked for
export function useMessageHistory(id: string, enabled = true) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<MessageHistoryEntry[]>({
    queryKey: messageKeys.history(clientId, id),
    queryFn: async () =>
      (isDemoMode()
        ? getMockHistory(id)
        : await MessagesAPI.getHistory(clientId, id)
      ).history,
    staleTime: 15 * 1000,
    enabled: !!clientId && !!id && enabled,
  });
}

// Write a comment — public (the customer will hear it) or private — closing nothing
export function useAddMessageEntry() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      body: string;
      visibility: MessageVisibility;
      callback?: "now" | "later";
    }) =>
      isDemoMode()
        ? Promise.resolve(addMockEntry(id, data))
        : MessagesAPI.addEntry(clientId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
      queryClient.invalidateQueries({ queryKey: messageKeys.details() });
      queryClient.invalidateQueries({
        queryKey: [...messageKeys.all, "entries"],
      });
    },
  });
}

// Change a comment's visibility, or delete it
export function useEditMessageEntry() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      entryId,
      ...change
    }: {
      id: string;
      entryId: string;
    } & ({ visibility: MessageVisibility } | { remove: true })) => {
      if ("remove" in change) {
        return isDemoMode()
          ? Promise.resolve(deleteMockEntry(id, entryId))
          : MessagesAPI.deleteEntry(clientId, id, entryId);
      }
      return isDemoMode()
        ? Promise.resolve(setMockEntryVisibility(id, entryId, change.visibility))
        : MessagesAPI.setEntryVisibility(clientId, id, entryId, change.visibility);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
      queryClient.invalidateQueries({ queryKey: messageKeys.details() });
      queryClient.invalidateQueries({
        queryKey: [...messageKeys.all, "entries"],
      });
    },
  });
}

// Close a request
export function useHandleMessage() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      resolution: Exclude<MessageResolution, "">;
      reply?: string;
      callback_at?: number;
    }) =>
      isDemoMode()
        ? Promise.resolve(handleMockMessage(id, data))
        : MessagesAPI.handleMessage(clientId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
      queryClient.invalidateQueries({ queryKey: messageKeys.details() });
      queryClient.invalidateQueries({
        queryKey: [...messageKeys.all, "entries"],
      });
      queryClient.invalidateQueries({
        queryKey: [...messageKeys.all, "history"],
      });
    },
  });
}

// Assign a request to a team member (or to nobody)
// Change a request's urgency, category or deadline
export function useTriageMessage() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...change
    }: {
      id: string;
      urgency?: MessageUrgency;
      category?: string;
      due_at?: number;
    }) =>
      isDemoMode()
        ? Promise.resolve(triageMockMessage(id, change))
        : MessagesAPI.triageMessage(clientId, id, change),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
      queryClient.invalidateQueries({ queryKey: messageKeys.details() });
      queryClient.invalidateQueries({
        queryKey: [...messageKeys.all, "history"],
      });
    },
  });
}

export function useAssignMessage() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      memberId,
      memberName = "",
    }: {
      id: string;
      memberId: string;
      /** Only read in demo mode, where no server resolves the id to a name. */
      memberName?: string;
    }) =>
      isDemoMode()
        ? Promise.resolve(assignMockMessage(id, memberId, memberName))
        : MessagesAPI.assignMessage(clientId, id, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
      queryClient.invalidateQueries({ queryKey: messageKeys.details() });
    },
  });
}

/**
 * Tick or untick a task. Applied to the cached request at once — a checkbox that
 * waits for the server reads as one that did not take the click — and put back
 * if the server refuses.
 */
export function useSetMessageTask() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  const patch = (messageId: string, taskId: string, done: boolean) => {
    const apply = (message: ClientMessage): ClientMessage =>
      message.id !== messageId
        ? message
        : {
            ...message,
            tasks: (message.tasks || []).map((task) =>
              task.id === taskId ? { ...task, done } : task,
            ),
          };
    queryClient.setQueriesData<{ messages: ClientMessage[]; total: number }>(
      { queryKey: messageKeys.lists() },
      (data) => data && { ...data, messages: data.messages.map(apply) },
    );
    queryClient.setQueriesData<{ message: ClientMessage }>(
      { queryKey: messageKeys.details() },
      (data) => data && { message: apply(data.message) },
    );
  };

  return useMutation({
    mutationFn: ({
      id,
      taskId,
      done,
    }: {
      id: string;
      taskId: string;
      done: boolean;
    }) =>
      isDemoMode()
        ? Promise.resolve(setMockTask(id, taskId, done))
        : MessagesAPI.setTask(clientId, id, taskId, done),
    onMutate: ({ id, taskId, done }) => patch(id, taskId, done),
    onError: (_error, { id, taskId, done }) => patch(id, taskId, !done),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.lists() });
      queryClient.invalidateQueries({ queryKey: messageKeys.details() });
    },
  });
}
